import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ClipboardList,
  Hourglass,
} from "lucide-react";

import { AdminShell } from "@/components/admin/admin-shell";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LocalDateTime } from "@/components/ui/local-datetime";
import { cn } from "@/lib/utils";

type TroubleCase = {
  testCaseId: string;
  title: string;
  category: string;
  failCount: number;
  totalRuns: number;
  failRate: number;
};

type SessionRow = {
  id: string;
  name: string;
  email: string;
  role: string;
  submittedAt: string | null;
  startedAt: string;
  pass: number;
  fail: number;
  other: number;
};

type QaListData = {
  totalSubmitted: number;
  totalInProgress: number;
  totalFails: number;
  rows: SessionRow[];
};

export function AdminQaPage() {
  const [list, setList] = useState<QaListData | null>(null);
  const [trouble, setTrouble] = useState<TroubleCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    Promise.all([
      fetch("/api/admin/qa/sessions", { credentials: "include", signal: controller.signal }),
      fetch("/api/admin/qa/trouble-cases?limit=10", { credentials: "include", signal: controller.signal }),
    ])
      .then(async ([listRes, troubleRes]) => {
        if (!listRes.ok || !troubleRes.ok) throw new Error("Failed to load QA data");
        const [listData, troubleData] = await Promise.all([
          listRes.json() as Promise<QaListData>,
          troubleRes.json() as Promise<TroubleCase[]>,
        ]);
        setList(listData);
        setTrouble(troubleData);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (err.name === "AbortError") return;
        setError(err.message);
        setLoading(false);
      });

    return () => controller.abort();
  }, []);

  return (
    <AdminShell>
      <main className="brand-bg min-h-[calc(100vh-3.5rem)]">
        <div className="mx-auto flex max-w-6xl flex-col gap-6 px-6 py-10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Link
              to="/admin"
              className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="size-3.5" />
              Back to orders
            </Link>
            <a
              href="/qa"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-background px-3 text-[12.5px] font-medium hover:bg-muted"
            >
              Run the test plan yourself ↗
            </a>
          </div>

          <header className="space-y-1">
            <p className="brand-eyebrow">Admin · Quality</p>
            <h1 className="brand-heading brand-heading-md">QA test runs</h1>
            <p className="brand-body max-w-prose">
              Submitted answers from every test run, plus a roll-up of which test
              cases are failing most often.
            </p>
          </header>

          {loading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : error ? (
            <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">
              {error}
            </div>
          ) : list ? (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <StatCard
                  icon={CheckCircle2}
                  label="Submitted runs"
                  value={list.totalSubmitted}
                />
                <StatCard
                  icon={Hourglass}
                  label="In progress"
                  value={list.totalInProgress}
                  tone="muted"
                />
                <StatCard
                  icon={AlertCircle}
                  label="Total fails recorded"
                  value={list.totalFails}
                  tone={list.totalFails > 0 ? "red" : "muted"}
                />
              </div>

              <section className="space-y-3">
                <h2 className="text-sm font-semibold text-muted-foreground">
                  Trouble cases · most-failed across all runs
                </h2>
                {trouble.length === 0 ? (
                  <Card className="px-5 py-6 text-sm text-muted-foreground">
                    No failed test cases yet.
                  </Card>
                ) : (
                  <Card className="overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[140px]">Case</TableHead>
                          <TableHead>Title</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead className="text-right">Fails</TableHead>
                          <TableHead className="text-right">Runs</TableHead>
                          <TableHead className="text-right">Fail rate</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {trouble.map((t) => (
                          <TableRow key={t.testCaseId}>
                            <TableCell className="font-mono text-xs">
                              {t.testCaseId}
                            </TableCell>
                            <TableCell className="font-medium">{t.title}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {t.category}
                            </TableCell>
                            <TableCell className="text-right font-semibold tabular-nums text-red-700">
                              {t.failCount}
                            </TableCell>
                            <TableCell className="text-right tabular-nums text-muted-foreground">
                              {t.totalRuns}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              <FailRatePill rate={t.failRate} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Card>
                )}
              </section>

              <section className="space-y-3">
                <h2 className="text-sm font-semibold text-muted-foreground">
                  Recent runs · newest first, last 50
                </h2>
                {list.rows.length === 0 ? (
                  <Card className="px-5 py-6 text-sm text-muted-foreground">
                    No QA runs yet.
                  </Card>
                ) : (
                  <Card className="overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tester</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Pass</TableHead>
                          <TableHead className="text-right">Fail</TableHead>
                          <TableHead className="text-right">Other</TableHead>
                          <TableHead className="text-right">When</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {list.rows.map((row) => (
                          <TableRow key={row.id} className="cursor-pointer">
                            <TableCell>
                              <Link to={`/admin/qa/${row.id}`} className="block min-w-0">
                                <div className="font-medium">{row.name}</div>
                                <div className="truncate text-xs text-muted-foreground">
                                  {row.email}
                                </div>
                              </Link>
                            </TableCell>
                            <TableCell>
                              <Link to={`/admin/qa/${row.id}`} className="text-xs text-muted-foreground">
                                {row.role}
                              </Link>
                            </TableCell>
                            <TableCell>
                              <Link to={`/admin/qa/${row.id}`}>
                                <StatusPill submitted={row.submittedAt !== null} />
                              </Link>
                            </TableCell>
                            <TableCell className="text-right tabular-nums text-emerald-700">
                              <Link to={`/admin/qa/${row.id}`}>{row.pass}</Link>
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              <Link to={`/admin/qa/${row.id}`}>
                                <span className={row.fail > 0 ? "font-semibold text-red-700" : "text-muted-foreground"}>
                                  {row.fail}
                                </span>
                              </Link>
                            </TableCell>
                            <TableCell className="text-right tabular-nums text-amber-700">
                              <Link to={`/admin/qa/${row.id}`}>{row.other}</Link>
                            </TableCell>
                            <TableCell className="text-right text-xs text-muted-foreground">
                              <Link to={`/admin/qa/${row.id}`}>
                                <LocalDateTime
                                  iso={row.submittedAt ?? row.startedAt}
                                  preset="medium"
                                />
                              </Link>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Card>
                )}
              </section>
            </>
          ) : null}
        </div>
      </main>
    </AdminShell>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof CheckCircle2;
  label: string;
  value: number;
  tone?: "muted" | "red";
}) {
  return (
    <Card className="flex items-center gap-4 p-5">
      <span
        className={cn(
          "flex size-10 shrink-0 items-center justify-center rounded-xl",
          tone === "red"
            ? "bg-red-50 text-red-700 ring-1 ring-inset ring-red-200/60"
            : tone === "muted"
              ? "bg-muted text-muted-foreground"
              : "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200/60",
        )}
      >
        <Icon className="size-4" />
      </span>
      <div className="min-w-0">
        <p className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
          {label}
        </p>
        <p className="text-center text-2xl font-semibold tabular-nums">{value}</p>
      </div>
    </Card>
  );
}

function StatusPill({ submitted }: { submitted: boolean }) {
  if (submitted) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10.5px] font-medium uppercase tracking-[0.06em] text-emerald-800 ring-1 ring-inset ring-emerald-200/60">
        Submitted
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-medium uppercase tracking-[0.06em] text-muted-foreground ring-1 ring-inset ring-border">
      <ClipboardList className="size-3" />
      In progress
    </span>
  );
}

function FailRatePill({ rate }: { rate: number }) {
  const pct = Math.round(rate * 100);
  const tone =
    rate >= 0.5
      ? "bg-red-50 text-red-800 ring-red-200/60"
      : rate >= 0.25
        ? "bg-amber-50 text-amber-800 ring-amber-200/60"
        : "bg-muted text-muted-foreground ring-border";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset",
        tone,
      )}
    >
      {pct}%
    </span>
  );
}
